/**
 * `accept` for photo pickers. Desktop browsers filter the file dialog by
 * extension, and Windows browsers don't count .heic as an image — so iPhone
 * photos copied to a PC were hidden behind "All files". The server converts
 * HEIC to JPEG, so it's safe to offer.
 *
 * Not on iPhone/iPad: listing HEIC there tells iOS to hand over the original
 * HEIC instead of converting to JPEG itself, which would shift every iOS
 * upload (including bulk imports of hundreds) onto the server's slower
 * one-at-a-time conversion.
 */
function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    // iPadOS reports itself as a Mac; touch support gives it away.
    || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export const PHOTO_ACCEPT = isAppleMobile() ? 'image/*' : 'image/*,.heic,.heif';

/** The server's own reason for a failed request (e.g. "That photo is too large…"), if it gave one. */
export function serverErrorMessage(err: unknown): string | null {
  const msg = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof msg === 'string' && msg.trim() ? msg : null;
}

/** "2 of 5 photos couldn't be uploaded: That photo is too large…" — reasons de-duplicated. */
export function describeUploadFailures(reasons: (string | null)[], total: number, noun = 'photo'): string {
  const failed = reasons.length;
  const plural = total === 1 ? noun : `${noun}s`;
  const head = failed === total
    ? `${total === 1 ? `The ${noun}` : `The ${plural}`} couldn't be uploaded`
    : `${failed} of ${total} ${plural} couldn't be uploaded`;
  const unique = [...new Set(reasons.filter((r): r is string => Boolean(r)))];
  return unique.length ? `${head}: ${unique.join(' ')}` : `${head}.`;
}
